import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { brandName } from "@mealmanager/legal-content";

export type ConsentState = "unknown" | "accepted" | "rejected";

type CookieConsentOptions = {
  cookieDomain?: string | null;
  cookieName?: string;
  maxAgeDays?: number;
};

type CookieConsentBannerProps = CookieConsentOptions & {
  privacyPolicyHref?: string;
  cookiePolicyHref?: string;
  acceptLabel?: string;
  declineLabel?: string;
  renderLink?: (props: { href: string; children: ReactNode; onClick?: () => void }) => ReactNode;
};

const DEFAULT_COOKIE_NAME = "cookie-consent";
const DEFAULT_MAX_AGE_DAYS = 180;
const OPEN_EVENT = "cookie-consent:open";
const CHANGE_EVENT = "cookie-consent:change";
const PENDING_EVENT = "cookie-consent:pending";

export const COOKIE_CONSENT_OPEN_EVENT = OPEN_EVENT;

function getCookieName(options?: CookieConsentOptions) {
  return options?.cookieName ?? DEFAULT_COOKIE_NAME;
}

function getDocumentCookie() {
  if (typeof document === "undefined") {
    return "";
  }
  return document.cookie ?? "";
}

function parseConsentState(value: string | null | undefined): ConsentState {
  return value === "accepted" || value === "rejected" ? value : "unknown";
}

function shouldUseNativePreferences() {
  return typeof window !== "undefined" && Capacitor.isNativePlatform();
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = getDocumentCookie().split(";").map((part) => part.trim());
  const match = parts.find((part) => part.startsWith(prefix));
  if (!match) {
    return null;
  }
  return decodeURIComponent(match.slice(prefix.length));
}

export function getStoredCookieConsent(options?: CookieConsentOptions): ConsentState {
  if (typeof window === "undefined") {
    return "unknown";
  }
  return parseConsentState(readCookie(getCookieName(options)));
}

export async function getStoredCookieConsentAsync(options?: CookieConsentOptions): Promise<ConsentState> {
  if (typeof window === "undefined") {
    return "unknown";
  }

  if (shouldUseNativePreferences()) {
    const { value } = await Preferences.get({ key: getCookieName(options) });
    return parseConsentState(value);
  }

  return getStoredCookieConsent(options);
}

export function isCookieConsentPending(options?: CookieConsentOptions): boolean {
  return getStoredCookieConsent(options) === "unknown";
}

export async function isCookieConsentPendingAsync(options?: CookieConsentOptions): Promise<boolean> {
  return (await getStoredCookieConsentAsync(options)) === "unknown";
}

function buildCookieString(value: Exclude<ConsentState, "unknown">, options?: CookieConsentOptions) {
  const segments = [
    `${encodeURIComponent(getCookieName(options))}=${encodeURIComponent(value)}`,
    "Path=/",
    `Max-Age=${Math.max(1, Math.round((options?.maxAgeDays ?? DEFAULT_MAX_AGE_DAYS) * 24 * 60 * 60))}`,
    "SameSite=Lax",
  ];

  if (options?.cookieDomain) {
    segments.push(`Domain=${options.cookieDomain}`);
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    segments.push("Secure");
  }

  return segments.join("; ");
}

export function setStoredCookieConsent(
  value: Exclude<ConsentState, "unknown">,
  options?: CookieConsentOptions
) {
  if (shouldUseNativePreferences()) {
    return Preferences.set({ key: getCookieName(options), value });
  }

  if (typeof document === "undefined") {
    return;
  }
  document.cookie = buildCookieString(value, options);
}

export function requestCookiePreferences() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(OPEN_EVENT));
  window.dispatchEvent(new CustomEvent<boolean>(PENDING_EVENT, { detail: true }));
}

export function subscribeToCookieConsent(
  callback: (state: ConsentState) => void,
  options?: CookieConsentOptions
) {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<ConsentState>).detail;
    if (detail) {
      callback(detail);
      return;
    }
    void getStoredCookieConsentAsync(options).then(callback);
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export function subscribeToCookieConsentPending(
  callback: (pending: boolean) => void,
  options?: CookieConsentOptions
) {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<boolean>).detail;
    if (typeof detail === "boolean") {
      callback(detail);
      return;
    }
    void isCookieConsentPendingAsync(options).then(callback);
  };
  window.addEventListener(PENDING_EVENT, handler);
  return () => window.removeEventListener(PENDING_EVENT, handler);
}

export function CookieConsentBanner({
  cookieDomain,
  cookieName,
  maxAgeDays,
  privacyPolicyHref = "/privacy",
  cookiePolicyHref = "/cookies",
  acceptLabel = "Accept",
  declineLabel = "Reject",
  renderLink,
}: CookieConsentBannerProps) {
  const [consent, setConsent] = useState<ConsentState>("unknown");
  const [mounted, setMounted] = useState(false);

  const options = {
    cookieDomain,
    cookieName,
    maxAgeDays,
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let cancelled = false;
    void getStoredCookieConsentAsync(options).then((storedConsent) => {
      if (cancelled) {
        return;
      }
      setConsent(storedConsent);
      setMounted(true);
      window.dispatchEvent(new CustomEvent<boolean>(PENDING_EVENT, { detail: storedConsent === "unknown" }));
    });

    const handleOpen = () => {
      setConsent("unknown");
      setMounted(true);
      window.dispatchEvent(new CustomEvent<boolean>(PENDING_EVENT, { detail: true }));
    };
    window.addEventListener(OPEN_EVENT, handleOpen);
    return () => {
      cancelled = true;
      window.removeEventListener(OPEN_EVENT, handleOpen);
    };
  }, [cookieDomain, cookieName, maxAgeDays]);

  const updateConsent = useCallback(
    async (value: Exclude<ConsentState, "unknown">) => {
      if (typeof window === "undefined") {
        return;
      }
      await setStoredCookieConsent(value, options);
      window.dispatchEvent(new CustomEvent<ConsentState>(CHANGE_EVENT, { detail: value }));
      window.dispatchEvent(new CustomEvent<boolean>(PENDING_EVENT, { detail: false }));
      setConsent(value);
    },
    [cookieDomain, cookieName, maxAgeDays]
  );

  if (!mounted || consent !== "unknown") {
    return null;
  }

  const privacyLink = renderLink ? renderLink({ href: privacyPolicyHref, children: "Privacy Policy" }) : (
    <a href={privacyPolicyHref} style={{ color: "#86efac" }}>Privacy Policy</a>
  );
  const cookieLink = renderLink ? renderLink({ href: cookiePolicyHref, children: "Cookie Policy" }) : (
    <a href={cookiePolicyHref} style={{ color: "#86efac" }}>Cookie Policy</a>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      style={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#111827",
        color: "#fff",
        padding: "16px 24px",
        borderRadius: 12,
        boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        width: "min(480px, calc(100% - 32px))",
        zIndex: 999,
      }}
    >
      <h2 id="cookie-consent-title" style={{ margin: "0 0 8px", fontSize: 18 }}>
        Cookie preferences
      </h2>
      <p id="cookie-consent-description" style={{ margin: "0 0 12px", lineHeight: 1.5 }}>
        We use necessary cookies to keep {brandName} working and optional analytics to understand usage.
        Read our {privacyLink} and{" "}
        {cookieLink}.
      </p>
      <div style={{ marginBottom: 16, lineHeight: 1.5 }}>
        <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Necessary</p>
        <p style={{ margin: "0 0 10px" }}>Authentication, security, and session management.</p>
        <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Analytics</p>
        <p style={{ margin: "0 0 10px" }}>Heap analytics only if you accept.</p>
        <p style={{ margin: 0 }}><strong>Sentry</strong> error monitoring may continue to operate for reliability and security.</p>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={() => updateConsent("accepted")}
          style={{
            flex: 1,
            minWidth: 120,
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.75)",
            borderRadius: 10,
            padding: "10px 16px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {acceptLabel}
        </button>
        <button
          type="button"
          onClick={() => updateConsent("rejected")}
          style={{
            flex: 1,
            minWidth: 120,
            background: "transparent",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.75)",
            borderRadius: 10,
            padding: "10px 16px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {declineLabel}
        </button>
      </div>
    </div>
  );
}
